// MultiStackLine (insight_stackbar_linechart): stacked bars + a line overlay.
// Drawn natively with the already-loaded Plotly — no vendored class exists for
// this type. Bar measures stack on the left axis; the last measure (when there
// are 2+) renders as a line on a right axis.

import { useEffect, useRef, useState } from 'react'
import { ensurePlotly } from './loader'
import { mapVisualData, dimensions, measures, toNumber, formatValue } from './dataUtils'
import type { NativeRendererProps } from './nativeRenderers'

export function ComboChart({ jsonData, visualLayout }: NativeRendererProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const md = mapVisualData(jsonData, visualLayout)
    const dims = dimensions(md)
    const ms = measures(md)
    if (!dims.length || !ms.length) {
      setErr('Needs a dimension and at least one measure.')
      return
    }
    const x = md.rows.map((r) => formatValue(r[dims[0].name], dims[0].type))

    ensurePlotly().then(() => {
      if (cancelled || !ref.current) return
      const P = (window as unknown as { Plotly?: { newPlot: (...a: unknown[]) => void; purge: (e: Element) => void } }).Plotly
      if (!P) { setErr('Plotly failed to load.'); return }
      const barMs = ms.length > 1 ? ms.slice(0, -1) : ms
      const lineM = ms.length > 1 ? ms[ms.length - 1] : null
      const traces: Record<string, unknown>[] = barMs.map((m) => ({
        type: 'bar', name: m.label, x, y: md.rows.map((r) => toNumber(r[m.name])),
      }))
      if (lineM) {
        traces.push({
          type: 'scatter', mode: 'lines+markers', name: lineM.label, x,
          y: md.rows.map((r) => toNumber(r[lineM.name])), yaxis: 'y2',
        })
      }
      const layout = {
        barmode: 'stack', margin: { t: 20, r: 50, b: 70, l: 55 }, showlegend: true,
        yaxis2: { overlaying: 'y', side: 'right', showgrid: false },
        paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
      }
      P.newPlot(ref.current, traces, layout, { displayModeBar: false, responsive: true })
    }).catch((e) => { if (!cancelled) setErr(String(e)) })

    return () => {
      cancelled = true
      const P = (window as unknown as { Plotly?: { purge: (e: Element) => void } }).Plotly
      if (ref.current && P) P.purge(ref.current)
    }
  }, [jsonData, visualLayout])

  if (err) return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-error, var(--color-error, #c33))', textAlign: 'center' }}>{err}</div>
  return <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 240 }} />
}

export default ComboChart
